import type { CuriumErrorCode } from './error-code';

/**
 * The Auth0 database connection's password policy ("good"): at least 8 characters and at least 3
 * of the 4 character types. Auth0 also refuses common passwords and passwords containing the
 * user's profile details, which only Auth0 can check, so its refusal stays authoritative. The
 * policy applies to new passwords only; an existing password keeps signing in.
 */
export const PASSWORD_POLICY = {
    minLength: 8,
    minCharacterTypes: 3,
    /** The characters Auth0 counts as special: space and the ASCII punctuation (password-sheriff). */
    symbols: ' !"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~',
} as const;

/**
 * What the user is told when Auth0 refuses a new password, by the error code the server sends.
 * The server throws these with `captureInSentry: false`: a refused password is the user's to change.
 */
export const PASSWORD_REFUSAL_MESSAGES = {
    'password.tooWeak': 'Use at least 8 characters and 3 of the 4 character types.',
    'password.tooCommon': 'Choose a less common password.',
    'password.containsUserInfo': 'Your password must not contain your personal information.',
    'password.rejected': 'This password does not meet the password requirements.',
} as const satisfies Partial<Record<CuriumErrorCode, string>>;

export type PasswordRefusalCode = keyof typeof PASSWORD_REFUSAL_MESSAGES;

export function isPasswordRefusalCode(code: unknown): code is PasswordRefusalCode {
    return typeof code === 'string' && Object.hasOwn(PASSWORD_REFUSAL_MESSAGES, code);
}
